import React, { useState } from 'react';
import { Button, Card, CardContent, LinearProgress, Paper, Typography, Box, IconButton, Tabs, Tab, FormControl, Select, MenuItem, Grid, Tooltip } from '@mui/material';
import { CloudUpload, Delete, Pause, PlayArrow, Refresh, Warning } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import Cookies from 'universal-cookie';
import { useAuth } from '../utils/useAuth';
import {compareKeys, naturalSortKey} from "../utils/utils";

const useStyles = makeStyles((theme) => ({
    root: {
    },
    uploadSection: {
        display: 'flex',
        gap: '20px',
        alignItems: 'flex-start',
        marginBottom: '30px',
    },
    uploadArea: {
        border: '2px dashed #ccc',
        borderRadius: '8px',
        padding: theme.spacing(3),
        textAlign: 'center',
        cursor: 'pointer',
        transition: '0.2s',
        flex: 1,
        minWidth: '300px',
        '&:hover': {
            borderColor: '#000000',
        },
    },
    uploadAreaDisabled: {
        border: '2px dashed #ccc',
        borderRadius: '8px',
        padding: theme.spacing(3),
        textAlign: 'center',
        cursor: 'not-allowed',
        opacity: 0.5,
        flex: 1,
        minWidth: '300px',
    },
    progressSection: {
        flex: 1,
        minWidth: '400px',
    },
    statsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '15px',
        marginBottom: '15px',
    },
    statCard: {
        padding: '12px',
        borderRadius: '4px',
        textAlign: 'center',
        backgroundColor: '#f2f2f2',
    },
    progressContainer: {
        marginTop: '10px',
    },
    controlButtons: {
        display: 'flex',
        gap: '10px',
        marginTop: '15px',
        justifyContent: 'flex-end',
    },
    tabsContainer: {
        marginTop: '30px',
    },
    fileList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '15px',
        marginTop: '20px',
    },
    fileCard: {
        position: 'relative',
        padding: '15px',
        borderRadius: '8px',
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
    },
    button: {
        backgroundColor: '#000000',
        color: 'white',
        boxShadow: '2px 2px #6f6f6f6b',
        fontFamily: "'GucciSans-Bold' !important",
        '&:hover': {
            backgroundColor: '#333333',
        },
    },
    resolutionWarningBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: '#fff3cd',
        color: '#856404',
        fontSize: '12px',
        fontWeight: 'bold',
        marginTop: '4px',
    },
}));

const FILE_STATUS = {
    PENDING: 'pending',
    UPLOADING: 'uploading',
    COMPLETED: 'completed',
    FAILED: 'failed',
    PAUSED: 'paused',
};

export default function UploadPhotostudioImages(props) {
    const classes = useStyles();
    const { getAccessToken } = useAuth();
    const fileInputRef = React.useRef(null);

    const [eventsList, setEventsList] = useState([]);
    const [files, setFiles] = useState([]);
    const [isPaused, setIsPaused] = useState(false);
    const [filterTab, setFilterTab] = useState('all');
    const [selectedEvent, setSelectedEvent] = useState(0);
    const [selectedEventLabel, setSelectedEventLabel] = useState('Select Event...');

    React.useEffect(() => {
        if (props.events && props.events.length > 0) {
            const eventList = [
                { id: 0, name: 'Select Event...' },
                ...props.events
            ];
            setEventsList(eventList);
            setSelectedEvent(props.events[0].id);
            setSelectedEventLabel(props.events[0].name);

            if (props.preselectedEventId) {
                setSelectedEvent(props.preselectedEventId);
            }
        }
    }, [props.events]);

    const handleChangeEvent = (e) => {
        setSelectedEvent(e.target.value);
        if (eventsList) {
            eventsList.forEach((event) => {
                if (event.id === e.target.value) {
                    setSelectedEventLabel(event.name);
                }
            });
        }
    };

    async function getAuthHeader() {
        try {
            const token = await getAccessToken();
            if (!token) throw new Error("Utente non autenticato");
            const cookies = new Cookies();
            const hashCode = cookies.get('hashCode') || '';
            return token + hashCode;
        } catch (err) {
            console.error("Errore getAuthHeader:", err);
            throw err;
        }
    }

    const handleFileSelect = (selectedFiles) => {
        const fileArray = Array.from(selectedFiles);
        const newFiles = fileArray.map((file, index) => ({
            id: Date.now() + index,
            file,
            name: file.name,
            size: file.size,
            status: FILE_STATUS.PENDING,
            progress: 0,
            error: null,
            resolutionWarning: null,
        }));

        setFiles(prev => [...prev, ...newFiles]);
    };

    const handleStartUpload = async () => {
        const pendingFiles = files.filter(f => f.status === FILE_STATUS.PENDING);
        const sortedFiles = pendingFiles.sort((a, b) => a.name.localeCompare(b.name));

        try {
            const filenames = sortedFiles.map(f => f.file.name);
            const authHeader = await getAuthHeader();

            const presignedUrlsResponse = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}photostudio/uploadurl?eventID=${selectedEvent}`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ filenames }),
                }
            );

            if (!presignedUrlsResponse.ok) {
                throw new Error(`HTTP ${presignedUrlsResponse.status}`);
            }

            const presignedUrlsData = await presignedUrlsResponse.json();
            if (!presignedUrlsData.success) {
                throw new Error('Failed to get presigned URLs');
            }

            sortedFiles.forEach((fileObj, index) => {
                updateFileStatus(fileObj.id, FILE_STATUS.UPLOADING, 0);
            });

            const uploadPromises = sortedFiles.map((fileObj, index) =>
                uploadFile(fileObj.id, presignedUrlsData.images[index], presignedUrlsData.images[index].orderNumber)
            );

            await Promise.all(uploadPromises);
        } catch (error) {
            console.error('Error during batch upload:', error);
            const pendingFilesNow = files.filter(f => f.status === FILE_STATUS.UPLOADING);
            pendingFilesNow.forEach(f => {
                updateFileStatus(f.id, FILE_STATUS.FAILED, null, error.message);
            });
        }
    };

    const uploadFile = async (fileId, imageData, orderNumber = 1) => {
        const fileObj = files.find(f => f.id === fileId);
        if (!fileObj) return;

        try {
            if (isPaused) {
                updateFileStatus(fileId, FILE_STATUS.PAUSED);
                return;
            }

            updateFileStatus(fileId, FILE_STATUS.UPLOADING, 25);

            if (isPaused) {
                updateFileStatus(fileId, FILE_STATUS.PAUSED);
                return;
            }

            const uploadResponse = await fetch(imageData.uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': imageData.contentType,
                },
                body: fileObj.file,
            });

            if (!uploadResponse.ok) {
                throw new Error(`S3 upload failed: ${uploadResponse.status}`);
            }

            updateFileStatus(fileId, FILE_STATUS.UPLOADING, 50);

            if (isPaused) {
                updateFileStatus(fileId, FILE_STATUS.PAUSED);
                return;
            }

            const authHeader = await getAuthHeader();
            const lambdaResponse = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}photostudio/processimage?eventID=${selectedEvent}`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        uuid: imageData.uuid,
                        extension: imageData.extension,
                        orderNumber: orderNumber,
                        filename: fileObj.file.name,
                    }),
                }
            );

            if (!lambdaResponse.ok) {
                throw new Error(`Lambda notification failed: ${lambdaResponse.status}`);
            }

            const responseData = await lambdaResponse.json();
            const resolutionWarning = responseData.resolution?.warning || false;

            setFiles(prev => prev.map(f =>
                f.id === fileId
                    ? { ...f, status: FILE_STATUS.COMPLETED, progress: 100, resolutionWarning }
                    : f
            ));
        } catch (error) {
            console.error(`Error uploading file ${fileId}:`, error);
            updateFileStatus(fileId, FILE_STATUS.FAILED, null, error.message);
        }
    };

    const updateFileStatus = (fileId, status, progress = null, error = null) => {
        setFiles(prev => prev.map(f =>
            f.id === fileId
                ? { ...f, status, progress: progress !== null ? progress : f.progress, error }
                : f
        ));
    };

    const handlePauseAll = () => {
        setIsPaused(!isPaused);
        if (!isPaused) {
            setFiles(prev => prev.map(f =>
                f.status === FILE_STATUS.UPLOADING ? { ...f, status: FILE_STATUS.PAUSED } : f
            ));
        } else {
            setFiles(prev => prev.map(f => {
                if (f.status === FILE_STATUS.PAUSED) {
                    uploadFile(f.id);
                    return { ...f, status: FILE_STATUS.UPLOADING };
                }
                return f;
            }));
        }
    };

    const handleCancelAll = () => {
        setFiles([]);
        setIsPaused(false);
    };

    const handleClearCompleted = () => {
        setFiles(prev => prev.filter(f => f.status !== FILE_STATUS.COMPLETED));
    };

    const handleGoToManageImages = () => {
        if (props.goToManage) {
            props.goToManage(selectedEvent);
        }
    };

    const handleDeleteFile = (fileId) => {
        setFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const handleRetryFile = (fileId) => {
        const fileObj = files.find(f => f.id === fileId);
        if (!fileObj) return;

        const sortedFiles = files.sort((a, b) => compareKeys(naturalSortKey(a.name), naturalSortKey(b.name)));
        const orderNumber = sortedFiles.findIndex(f => f.id === fileId) + 1;

        updateFileStatus(fileId, FILE_STATUS.PENDING, 0, null);
        uploadFile(fileId, orderNumber);
    };

    const getStats = () => {
        return {
            uploading: files.filter(f => f.status === FILE_STATUS.UPLOADING).length,
            completed: files.filter(f => f.status === FILE_STATUS.COMPLETED).length,
            failed: files.filter(f => f.status === FILE_STATUS.FAILED).length,
            pending: files.filter(f => f.status === FILE_STATUS.PENDING).length,
        };
    };

    const getOverallProgress = () => {
        if (files.length === 0) return 0;
        const totalProgress = files.reduce((sum, f) => sum + (f.progress || 0), 0);
        return Math.round(totalProgress / files.length);
    };

    const stats = getStats();
    const overallProgress = getOverallProgress();

    const getFilteredFiles = () => {
        let filtered;
        switch (filterTab) {
            case 'uploading':
                filtered = files.filter(f => f.status === FILE_STATUS.UPLOADING);
                break;
            case 'completed':
                filtered = files.filter(f => f.status === FILE_STATUS.COMPLETED);
                break;
            case 'failed':
                filtered = files.filter(f => f.status === FILE_STATUS.FAILED);
                break;
            case 'pending':
                filtered = files.filter(f => f.status === FILE_STATUS.PENDING);
                break;
            default:
                filtered = files;
        }
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
    };

    const filteredFiles = getFilteredFiles();

    return (
        <div className={classes.root}>
            <Grid item xs={12}>
                <h3 className='gucci_text' style={{ margin: 0 }}>
                    Upload PhotoStudio Images
                </h3>

                {eventsList && eventsList.length > 1 && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 2,
                            mb: 3,
                        }}
                    >
                        <FormControl sx={{ minWidth: 400 }}>
                            <Select
                                fullWidth
                                className='gucci_text select_filter'
                                value={selectedEvent}
                                onChange={handleChangeEvent}
                            >
                                {eventsList.map((event) => (
                                    <MenuItem key={event.id} value={event.id}>
                                        {event.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                <Box className={classes.uploadSection}>
                <Paper
                    className={selectedEvent !== 0 ? classes.uploadArea : classes.uploadAreaDisabled}
                    onClick={() => selectedEvent !== 0 && fileInputRef.current?.click()}
                    onDragOver={(e) => {
                        if (selectedEvent !== 0) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    onDrop={(e) => {
                        if (selectedEvent !== 0) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFileSelect(e.dataTransfer.files);
                        }
                    }}
                    sx={{ bgcolor: 'transparent', boxShadow: 'none' }}
                >
                    <CloudUpload style={{ fontSize: '40px', color: selectedEvent !== 0 ? '#000000' : '#ccc', marginBottom: '10px' }} />
                    <Typography variant="h6" className="gucci_text_bold">
                        Choose a file or drag & drop it here.
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {selectedEvent !== 0 ? 'Add photostudio images.' : 'Please select an event first'}
                    </Typography>
                    <Button
                        variant="contained"
                        className={classes.button}
                        startIcon={<CloudUpload sx={{marginRight: '1rem'}} />}
                        style={{ marginTop: '15px' }}
                        disabled={selectedEvent === 0 || stats.uploading > 0}
                        onClick={(e) => {
                            e.stopPropagation();
                            selectedEvent !== 0 && fileInputRef.current?.click();
                        }}
                    >
                        Browse File
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileSelect(e.target.files)}
                    />
                </Paper>

                {files.length > 0 && (
                    <Box className={classes.progressSection}>
                        <Box className={classes.statsContainer}>
                            <Paper className={classes.statCard}>
                                <Typography variant="h6" style={{ color: '#000000' }}>
                                    {stats.uploading}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Uploading
                                </Typography>
                            </Paper>
                            <Paper className={classes.statCard}>
                                <Typography variant="h6" style={{ color: '#000000' }}>
                                    {stats.completed}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Completed
                                </Typography>
                            </Paper>
                            <Paper className={classes.statCard}>
                                <Typography variant="h6" style={{ color: '#ca0000' }}>
                                    {stats.failed}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Failed
                                </Typography>
                            </Paper>
                            <Paper className={classes.statCard}>
                                <Typography variant="h6" style={{ color: '#000000' }}>
                                    {stats.pending}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Pending
                                </Typography>
                            </Paper>
                        </Box>

                        <Box style={{ marginTop: '10px' }}>
                            <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                <Typography variant="body2" className="gucci_text_bold">
                                    Uploading {stats.uploading + stats.pending} of {files.length}
                                </Typography>
                                <Typography variant="body2">{overallProgress}% complete</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={overallProgress} />
                        </Box>

                        <Box className={classes.controlButtons}>
                            {stats.pending > 0 && stats.uploading === 0 && (
                                <Button
                                    variant="contained"
                                    className={classes.button}
                                    disabled={stats.uploading > 0}
                                    startIcon={<CloudUpload sx={{marginRight: '1rem'}} />}
                                    onClick={handleStartUpload}
                                >
                                    Upload
                                </Button>
                            )}
                            {stats.uploading > 0 && (
                                <Button
                                    variant="contained"
                                    className={classes.button}
                                    startIcon={isPaused ? <PlayArrow /> : <Pause />}
                                    onClick={handlePauseAll}
                                >
                                    {isPaused ? 'Resume All' : 'Pause All'}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                style={{ backgroundColor: '#757575', boxShadow: '2px 2px #6f6f6f6b' }}
                                onClick={handleCancelAll}
                            >
                                Cancel All
                            </Button>
                            {stats.completed > 0 && (
                                <Button
                                    variant="outlined"
                                    style={{ borderColor: '#000000', color: '#000000' }}
                                    onClick={handleClearCompleted}
                                >
                                    Clear Completed
                                </Button>
                            )}
                            {stats.completed > 0 && (
                                <Button
                                    variant="outlined"
                                    style={{ borderColor: '#000000', color: '#000000' }}
                                    onClick={handleGoToManageImages}
                                >
                                    Manage Images
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            {files.length > 0 && (
                <Box className={classes.tabsContainer}>
                    <Tabs
                        value={filterTab}
                        onChange={(e, value) => setFilterTab(value)}
                        sx={{
                            "& .MuiTab-root": {
                                color: "#666",
                            },
                            "& .MuiTab-root.Mui-selected": {
                                color: "black",
                                backgroundColor: "#e0e0e0",
                            },
                            "& .MuiTabs-indicator": {
                                backgroundColor: "black",
                            },
                        }}
                    >
                        <Tab label={`All (${files.length})`} value="all" />
                        <Tab label={`Uploading (${stats.uploading})`} value="uploading" />
                        <Tab label={`Completed (${stats.completed})`} value="completed" />
                        <Tab label={`Failed (${stats.failed})`} value="failed" />
                        <Tab label={`Pending (${stats.pending})`} value="pending" />
                    </Tabs>

                    <Box className={classes.fileList}>
                        {filteredFiles.map((fileObj) => (
                            <Card key={fileObj.id} className={classes.fileCard}>
                                <CardContent style={{ padding: '12px' }}>
                                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <Box style={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="body2"
                                                className="gucci_text_bold"
                                                style={{ wordBreak: 'break-word' }}
                                            >
                                                {fileObj.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {(fileObj.size / (1024 * 1024)).toFixed(2)} MB
                                            </Typography>
                                        </Box>
                                        <Box style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                                            {fileObj.status === FILE_STATUS.FAILED && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRetryFile(fileObj.id)}
                                                    title="Retry upload"
                                                    style={{ color: '#FF9800' }}
                                                >
                                                    <Refresh fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteFile(fileObj.id)}
                                                title="Delete file"
                                                disabled={stats.uploading > 0}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Box style={{ marginBottom: '8px' }}>
                                        {fileObj.status === FILE_STATUS.UPLOADING && (
                                            <Typography variant="caption" style={{ color: '#000000' }}>
                                                Uploading... {fileObj.progress}%
                                            </Typography>
                                        )}
                                        {fileObj.status === FILE_STATUS.COMPLETED && (
                                            <Box>
                                                <Typography variant="caption" style={{ color: '#000000', fontWeight: 'bold' }}>
                                                    ✓ Completed
                                                </Typography>
                                                {fileObj.resolutionWarning && (
                                                    <Box className={classes.resolutionWarningBadge}>
                                                        <Tooltip title="Image not in size 3840 X 2160" arrow>
                                                            <Warning fontSize="small" style={{ cursor: 'pointer' }} />
                                                        </Tooltip>
                                                        Resolution warning
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                        {fileObj.status === FILE_STATUS.FAILED && (
                                            <Typography variant="caption" style={{ color: '#ca0000' }}>
                                                ✗ Failed: {fileObj.error}
                                            </Typography>
                                        )}
                                        {fileObj.status === FILE_STATUS.PENDING && (
                                            <Typography variant="caption" style={{ color: '#000000' }}>
                                                Pending...
                                            </Typography>
                                        )}
                                        {fileObj.status === FILE_STATUS.PAUSED && (
                                            <Typography variant="caption" style={{ color: '#999' }}>
                                                ⏸ Paused
                                            </Typography>
                                        )}
                                    </Box>

                                    {(fileObj.status === FILE_STATUS.UPLOADING || fileObj.progress > 0) && (
                                        <Box className={classes.progressContainer}>
                                            <LinearProgress variant="determinate" value={fileObj.progress} />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </Box>
            )}
            </Grid>
        </div>
    );
}
